import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LogIn, MailQuestion } from "lucide-react";
import { ART } from "@/lib/artwork";
import { SEO } from "@/components/SEO";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
// Redirect always uses window.location.origin so preview + production
// domains work identically without env-var gymnastics.

/**
 * LoginPage — Google Sign-In entry point. Unrecognised emails see a
 * Contact Us CTA per platform policy (invite-only signups).
 * The `?from=login` marker on the callback URL tells AuthCallback to use
 * the invite-only `/auth/emergent-google` flow (which 404s instead of
 * auto-creating accounts) so we can route the user to /contact.
 */
export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [notRegistered, setNotRegistered] = useState(false);
  const isAr = i18n.language === "ar";

  useEffect(() => {
    if (new URLSearchParams(location.search).get("not_registered") === "1") {
      setNotRegistered(true);
    }
  }, [location.search]);

  const signIn = () => {
    const redirectUrl = window.location.origin + "/auth/callback?from=login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-[70vh] flex items-center justify-center p-6" data-testid="login-page">
      <SEO title={isAr ? "تسجيل الدخول" : "Sign in"} description="Sign in to The Saudi Gate." path="/login" noindex />
      <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden>
        <img src={ART.soft} alt="" className="h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
      </div>
      <Card className="w-full max-w-md rounded-2xl border border-border p-8 space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-primary">
            {isAr ? "بوابة السعودية" : "The Saudi Gate"}
          </p>
          <h1 className="font-display text-2xl font-semibold mt-2">
            {isAr ? "تسجيل الدخول" : "Sign in"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isAr
              ? "استخدم حساب Google المرتبط بملفك في Saudi Gate."
              : "Use the Google account linked to your Saudi Gate profile."}
          </p>
        </div>

        {notRegistered ? (
          <div
            className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3"
            data-testid="login-not-registered"
          >
            <div className="flex items-start gap-2">
              <MailQuestion className="h-4 w-4 text-amber-700 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-900">
                  {isAr
                    ? "لم يتم ربط حساب Google هذا بعد."
                    : "This Google account isn't linked yet."}
                </p>
                <p className="text-amber-800 mt-1">
                  {isAr
                    ? "إذا كنت تتوقع الوصول، تواصل معنا وسنساعدك على الانضمام."
                    : "If you're expecting access, contact us and we'll help you join."}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/contact" className="flex-1">
                <Button className="w-full btn-brand rounded-xl" data-testid="login-contact-us">
                  {isAr ? "تواصل معنا" : "Contact us"}
                </Button>
              </Link>
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setNotRegistered(false)}
                data-testid="login-try-again"
              >
                {isAr ? "حاول مجددًا" : "Try again"}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={signIn}
            className="w-full btn-brand rounded-xl gap-2 h-11"
            data-testid="login-google"
          >
            <LogIn className="h-4 w-4" />
            {isAr ? "تسجيل الدخول عبر Google" : "Sign in with Google"}
          </Button>
        )}

        <p className="text-[11px] text-center text-muted-foreground">
          {isAr
            ? "بتسجيل الدخول فإنك توافق على شروط وسياسة الخصوصية."
            : "By signing in you agree to the Saudi Gate terms and privacy notice."}
        </p>
      </Card>
    </div>
  );
}
